import { useEffect } from "react";
import { ArrowLeft, ArrowRight, BookOpen, Crown, Package, Truck, Clock, MapPin } from "lucide-react";

export const GUIDES = [
  {
    slug: "how-to-prevent-razor-bumps",
    title: "How to Prevent Razor Bumps: A Simple Guide for Men",
    short: "Why razor bumps happen and the shaving habits that help keep skin smooth.",
    meta: "Razor bumps on your face or neck? Learn what causes them and simple shaving habits that help men keep skin smooth and comfortable.",
    sections: [
      ["What are razor bumps?", "Razor bumps are small, irritated bumps that can show up after shaving, most often on the neck, jawline and cheeks. They happen when freshly cut hairs curl back and grow into the skin instead of out of it. Men with curly or coarse hair tend to get them more often."],
      ["Prep before you shave", "Wash your face first to clear away oil and dirt, then soften the hair with warm water for a minute or two. A gentle cleanser such as our Creamy Cleansing Balm helps melt away dirt and impurities while leaving skin feeling soft and conditioned."],
      ["Shave smarter, not closer", "Use a clean, sharp blade and shave in the direction your hair grows. Use light strokes, rinse the blade often and don't pull your skin tight. Going over the same spot again and again, or shaving against the grain, cuts the hair below the skin line, and that is when bumps are more likely."],
      ["Care for your skin after", "Rinse with cool water and pat dry with a clean towel instead of rubbing. Follow with a soothing, lightweight moisturizer. Our Oil-Free Moisturizer hydrates with hyaluronic acid, glycerin, niacinamide and pro-vitamin B5, without a greasy feel."],
      ["Exfoliate a few times a week", "Gentle exfoliation 2–3 times a week helps clear dead skin so hairs can grow out more easily. Don't overdo it, because exfoliating every day can leave skin irritated."],
      ["Hands off", "Avoid picking or squeezing bumps. It can irritate the skin and lead to marks that take longer to fade."],
    ],
    products: ["Creamy Cleansing Balm", "Ingrown Hair Serum", "Oil-Free Moisturizer"],
  },
  {
    slug: "ingrown-hairs-men-face-neck",
    title: "Ingrown Hairs on the Face & Neck: Causes and Care",
    short: "What causes ingrown hairs and a simple daily routine to care for your skin.",
    meta: "What causes ingrown hairs on a man's face and neck, and a simple daily routine to help care for skin prone to ingrowns.",
    sections: [
      ["Why ingrown hairs happen", "An ingrown hair is a hair that grows back into the skin instead of rising out of it. Dead skin cells can clog the follicle and trap the hair, and shaving leaves a sharp tip that can curl back in. They often look like small red or dark bumps, and sometimes you can see the hair under the skin."],
      ["Keep the area clean", "Cleanse daily to keep oil, sweat and dead skin from building up around the hair follicles. Gentle is better than harsh. Stripping your skin can leave it irritated."],
      ["Use an exfoliating treatment", "Ingredients like salicylic acid and glycolic acid help exfoliate the surface of the skin. Our Ingrown Hair Serum pairs an exfoliating complex of salicylic and glycolic acid with tea tree oil, allantoin and aloe vera. It helps address the appearance of ingrown hairs while it hydrates and soothes. Apply a few drops after shaving along the jaw and neck."],
      ["Moisturize and guard", "Finish with an oil-free moisturizer to keep skin comfortable. In the morning, wear a broad-spectrum sunscreen such as our Mineral SPF 50, because sun exposure can make marks from old bumps more noticeable."],
      ["When to see a professional", "If bumps become painful, swollen, filled with pus, or keep coming back, talk to a dermatologist or doctor. This guide is general skincare information, not medical advice."],
    ],
    products: ["Ingrown Hair Serum", "Oil-Free Moisturizer", "Mineral SPF 50"],
  },
  {
    slug: "simple-mens-skincare-routine",
    title: "The Simple Men's Skincare Routine: Rise, Refine, Restore, Reign",
    short: "A no-nonsense, few-minute routine for clean-shaven and bearded men.",
    meta: "A simple men's skincare routine in four steps: cleanse, treat, hydrate and protect. Easy for clean-shaven and bearded men.",
    sections: [
      ["1. Rise: wash (morning and night)", "Start every routine by washing away dirt, oil and sunscreen. Our Creamy Cleansing Balm turns into a silky oil as you massage it in, then rinses clean with warm water."],
      ["2. Refine: treat (as needed)", "Shave often? An ingrown hair serum after shaving helps with the look of bumps. Want a weekly reset? An exfoliating clay mask, used 1–2 times a week, helps brighten and smooth dull, uneven skin."],
      ["3. Restore: moisturize (every day)", "Every skin type needs moisture, even oily skin. An oil-free moisturizer keeps skin balanced without shine. Bearded? Work it into the skin under your beard too."],
      ["4. Reign: sunscreen (every morning)", "Sunscreen is the easiest step to skip and one of the most important. A broad-spectrum mineral SPF 50 helps protect against UVA and UVB rays."],
      ["Pick your routine", "Clean-shaven: The Polished Prince (Rise, Restore, Reign). Bearded: The Majestic Mane (adds the exfoliating mask). Prone to razor bumps: The Regal Glow (adds the Ingrown Hair Serum)."],
    ],
    products: ["Creamy Cleansing Balm", "Blue Clay Exfoliation Mask", "Oil-Free Moisturizer", "Mineral SPF 50"],
  },
];

const GOLD = "text-[#c5a059]";

export const GuidesSection = () => {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 700);
    return () => clearTimeout(t);
  }, []);
  return (
    <section id="guides" data-testid="guides-section" className="scroll-mt-48 sm:scroll-mt-28 max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
      <p className={`text-xs uppercase tracking-[0.3em] ${GOLD}`}>The King's Library</p>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#f5f5f0]">Know your skin, King.</h2>
      <p className="mt-4 max-w-xl text-[#f5f5f0]/75 font-light leading-relaxed">Straight answers about razor bumps, ingrown hairs and a routine that fits real life.</p>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {GUIDES.map((g, i) => (
          <a key={g.slug} href={`/guides/${g.slug}`} data-testid={`guide-card-${i}`}
            className="group flex flex-col rounded-sm border border-white/10 bg-[#121212] p-7 hover:border-[#c5a059]/60 hover:-translate-y-1 transition-[transform,border-color] duration-500">
            <BookOpen size={20} className={GOLD} />
            <h3 className="mt-5 font-serif text-xl text-[#f5f5f0] leading-snug">{g.title}</h3>
            <p className="mt-3 flex-1 text-sm text-[#f5f5f0]/70 font-light leading-relaxed">{g.short}</p>
            <span className={`mt-6 inline-flex items-center gap-1 text-xs uppercase tracking-wider ${GOLD}`}>Read the guide <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></span>
          </a>
        ))}
      </div>
    </section>
  );
};

const SHIP = [
  [Clock, "Ships in 7–10 days", "Pre-orders and orders ship within 7–10 days."],
  [Truck, "Free shipping over $100", "Free on orders over $100 and always free on The Majestic Mane. Otherwise a flat $8.95."],
  [MapPin, "Shipping within the USA", "We currently ship to addresses in the United States."],
  [Package, "Tax at checkout", "Sales tax, if any, is calculated at secure checkout."],
];

export const ShippingInfo = () => (
  <section id="shipping" data-testid="shipping-info" className="scroll-mt-48 sm:scroll-mt-28 border-y border-white/10 bg-[#0d0d0d] py-20">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <p className={`text-xs uppercase tracking-[0.3em] ${GOLD}`}>Royal Delivery</p>
      <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight text-[#f5f5f0]">From our hands to your throne.</h2>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {SHIP.map(([Icon, t, d], i) => (
          <div key={t} data-testid={`shipping-point-${i}`} className="rounded-sm border border-[#c5a059]/20 bg-[#121212] p-6">
            <Icon size={20} className={GOLD} />
            <h3 className="mt-4 font-serif text-lg text-[#f5f5f0]">{t}</h3>
            <p className="mt-1.5 text-sm text-[#f5f5f0]/75 font-light leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const setMeta = (title, desc) => {
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (m) m.setAttribute("content", desc);
};

export const GuidePage = ({ slug }) => {
  const g = GUIDES.find((x) => x.slug === slug);
  useEffect(() => {
    if (g) setMeta(`${g.title} | Royal King Skin Co.`, g.meta);
    window.scrollTo(0, 0);
  }, [g]);
  if (!g) { window.location.replace("/#guides"); return null; }
  const others = GUIDES.filter((x) => x.slug !== slug);
  return (
    <div className="App grain min-h-screen bg-[#0a0a0a] text-[#f5f5f0]">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" data-testid="guide-home-link" className="flex items-center gap-3">
            <img src="/logo-200.webp" alt="Royal King Skin Co." className="h-11 w-auto" />
            <span className="hidden sm:block font-serif text-lg">Royal King Skin Co.</span>
          </a>
          <a href="/#guides" data-testid="guide-back-link" className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] ${GOLD}`}><ArrowLeft size={14} /> All Guides</a>
        </div>
      </header>
      <article data-testid="guide-article" className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
        <p className={`text-xs uppercase tracking-[0.3em] ${GOLD}`}>The Royal Guides</p>
        <h1 data-testid="guide-title" className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-tight">{g.title}</h1>
        <p className="mt-5 text-lg text-[#f5f5f0]/80 font-light leading-relaxed">{g.short}</p>
        {g.sections.map(([h, p]) => (
          <section key={h} className="mt-10">
            <h2 className="font-serif text-2xl text-[#f5f5f0]">{h}</h2>
            <p className="mt-3 text-base text-[#f5f5f0]/80 font-light leading-relaxed">{p}</p>
          </section>
        ))}
        <div data-testid="guide-products" className="mt-14 rounded-sm border border-[#c5a059]/40 bg-[#121212] p-7">
          <p className={`text-[11px] uppercase tracking-[0.3em] ${GOLD}`}>Featured in this guide</p>
          <p className="mt-3 font-serif text-xl">{g.products.join(" · ")}</p>
          <a href="/#products" data-testid="guide-shop-link" className="mt-6 inline-flex items-center gap-2 rounded-sm bg-[#c5a059] px-7 py-3.5 text-sm font-medium text-black hover:bg-[#d4af37] transition-colors">
            <Crown size={16} /> Shop the Routines
          </a>
          <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-[#f5f5f0]/50">Ships in 7–10 days · Free over $100</p>
        </div>
        <p className="mt-10 text-xs text-[#f5f5f0]/45">General skincare information only, not medical advice. Stop use if irritation occurs and talk to a dermatologist about persistent skin concerns.</p>
        <div className="mt-14 border-t border-white/10 pt-10">
          <p className={`text-[11px] uppercase tracking-[0.3em] ${GOLD}`}>Keep reading</p>
          {others.map((o, i) => (
            <a key={o.slug} href={`/guides/${o.slug}`} data-testid={`guide-next-${i}`} className="mt-4 flex items-center justify-between gap-4 font-serif text-lg hover:text-[#c5a059] transition-colors">
              {o.title} <ArrowRight size={16} className="shrink-0" />
            </a>
          ))}
        </div>
      </article>
    </div>
  );
};
